/**
 * How the user reaches an agent on the second channels — the agent's email
 * address and toll-free SMS number, plus display name — surfaced on the
 * Account page when a channel is on.
 *
 * Per-agent (U15): `agentContactFor(slug)` replaces the old Uno-only
 * constant. Stage-specific facts:
 *  - **Email domain:** prod uses the apex `agents.oculusmind.ai`; beta and
 *    local use `agents-beta.oculusmind.ai` (mirrors infra
 *    `stage-config.inboundEmailSubdomain`). An agent's inbound address
 *    exists only once its A2A runtime is registered in that stage's
 *    `a2aAgentRuntimeIds` — `EMAIL_ENABLED` mirrors that (FR-CHAN-3).
 *  - **SMS number:** the stage's toll-free number, hard-coded here (the
 *    number is provisioned in infra/SSM and never sent to the browser).
 *    All SMS routes to Vera (infra `sms.defaultAgentId`, user decision
 *    2026-07-29); a dedicated number for another agent lands in
 *    `SMS_NUMBERS` when registered (infra U20's number→agent map is ready).
 */
import { AGENTS, isAgentSlug, type AgentSlug } from './registry';
import { isProd } from '../config/stage';

const EMAIL_DOMAIN = isProd ? 'agents.oculusmind.ai' : 'agents-beta.oculusmind.ai';

/** Agents with a registered inbound-email/A2A identity (mirrors infra
 *  `a2aAgentRuntimeIds`, identical in both stages since the prod launch
 *  2026-07-07: `/a2a/vera` + `vera@agents.oculusmind.ai` exist in prod). */
const EMAIL_ENABLED: readonly AgentSlug[] = ['uno', 'vera'];

/** Per-agent SMS numbers (E.164), per stage. The shared stage toll-free
 *  number is Vera's: infra routes every inbound text to her. */
const SMS_NUMBERS: Readonly<Partial<Record<AgentSlug, string>>> = {
  vera: isProd ? '+18668102446' : '+18556345405',
};

export interface AgentContact {
  readonly slug: AgentSlug;
  readonly name: string;
  /** Address the user emails to reach the agent (stage-correct domain);
   *  undefined until the agent's inbound identity is registered. */
  readonly email?: string;
  /** Toll-free number the agent texts from / the user texts to (E.164);
   *  undefined until the agent has a dedicated number. */
  readonly smsNumber?: string;
  /** Public profile page, embedded in the vCard (FR-CHAN-6: always the
   *  agent's OWN url). */
  readonly profileUrl: string;
}

function agentContactFor(slug: string): AgentContact {
  if (!isAgentSlug(slug)) {
    // P5: an unknown slug is a programming error, not a render state.
    throw new Error(`agentContactFor: unknown agent slug '${slug}'`);
  }
  const agent = AGENTS[slug];
  const email = EMAIL_ENABLED.includes(slug) ? `${slug}@${EMAIL_DOMAIN}` : undefined;
  const smsNumber = SMS_NUMBERS[slug];
  return {
    slug,
    name: agent.name,
    ...(email !== undefined ? { email } : {}),
    ...(smsNumber !== undefined ? { smsNumber } : {}),
    profileUrl: `https://oculusmind.ai/agent/${slug}`,
  };
}

/** The generalist's contact — the Account "Email my agents" section renders
 *  this as its example address. */
export const emailAgentContact = agentContactFor('uno') as AgentContact & {
  readonly email: string;
};

/** The agent behind "Text my agents": inbound texts on the shared stage
 *  toll-free number route to Vera, so the SMS pills and vCard are hers.
 *  Callers that iterate agents use {@link agentContactFor} directly. */
export const smsAgentContact = agentContactFor('vera') as Required<AgentContact>;
